<template>
  <div class="customer-page">
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Đang tải dữ liệu...</p>
    </div>
    <div class="customer-header">
      <h1>Quản lý Khách hàng</h1>
      <div class="button-action">
        <button class="btn-add" @click="viewCreateCustomerModal">
          + Thêm khách hàng
        </button>
        <div class="action">
          <button @click="triggerFileInput" class="btn-add">
            + Nhập khách hàng
          </button>
          <input
            type="file"
            ref="fileInput"
            style="display: none"
            accept=".xlsx, .xls, .csv"
            @change="handleFileImport"
          />
        </div>
      </div>
    </div>
    <!-- FILTER -->
    <div class="customer-filter">
      <input
        v-model="search"
        type="text"
        placeholder="Tìm kiếm khách hàng..."
      />

      <select v-model="status">
        <option value="">Tất cả trạng thái</option>
        <option value="1">Hoạt động</option>
        <option value="0">Tạm ngưng</option>
      </select>

      <button class="btn-search" @click="fetchCustomers">Tìm kiếm</button>

      <!-- Toggle columns -->
      <div class="column-toggle">
        <button @click="showColumnPanel = !showColumnPanel">
          ⚙️ Cột hiển thị
        </button>

        <div v-if="showColumnPanel" class="column-panel">
          <label v-for="col in columns" :key="col.key">
            <input type="checkbox" v-model="col.visible" />
            {{ col.label }}
          </label>
        </div>
      </div>
    </div>

    <!-- TABLE -->
    <div class="customer-table">
      <table>
        <thead>
          <tr>
            <th>#</th>
            <th v-for="col in visibleColumns" :key="col.key">
              {{ col.label }}
            </th>
            <th>Hành động</th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="(item, index) in customers" :key="item.id">
            <td>{{ index + 1 }}</td>

            <td
              v-for="col in visibleColumns"
              :key="col.key"
              v-html="renderValue(item, col)"
            ></td>

            <td>
              <button class="btn-edit" @click="viewDetailCustomer(item)">
                Xem
              </button>
              <button class="btn-delete">Sửa</button>
            </td>
          </tr>

          <tr v-if="!customers.length">
            <td colspan="100%" class="empty">Không có dữ liệu</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-if="pagination.total > 0" class="pagination">
      <div class="pagination-info">
        Hiển thị {{ pagination.from }} - {{ pagination.to }} trong tổng số
        {{ pagination.total }} khách hàng
      </div>
      <div class="pagination-controls">
        <button
          @click="changePage(pagination.current_page - 1)"
          :disabled="pagination.current_page === 1"
          class="btn btn-outline btn-sm"
        >
          Trước
        </button>

        <button
          v-for="page in visiblePages"
          :key="page"
          @click="changePage(page)"
          :class="[
            'btn',
            'btn-sm',
            page === pagination.current_page ? 'btn-primary' : 'btn-outline',
          ]"
        >
          {{ page }}
        </button>

        <button
          @click="changePage(pagination.current_page + 1)"
          :disabled="pagination.current_page === pagination.last_page"
          class="btn btn-outline btn-sm"
        >
          Sau
        </button>
      </div>
    </div>

    <CreateCustomerModal
      v-if="showCreateCustomerModal"
      @close="showCreateCustomerModal = false"
      @created="fetchCustomers"
    />

    <CustomerDetailModal
      v-if="showCustomerDetailModal"
      :customer="selectedCustomer"
      @close="showCustomerDetailModal = false"
    />
  </div>
</template>
<script setup>
import { useToast } from "@/composables/useToast";
import { adminApi, importApi } from "@/services/api";
import { computed, onMounted, ref } from "vue";
import { formatCurrency } from "@/utils/helpers";
import { usePagination } from "@/composables/usePagination";
import CustomerDetailModal from "@/components/modal/customer/CustomerDetailModal.vue";
import CreateCustomerModal from "@/components/modal/customer/CreateCustomerModal.vue";

const fileInput = ref(null);
const toast = useToast();
const loading = ref(false);
const customers = ref([]);
const search = ref("");
const status = ref("");
const showColumnPanel = ref(false);
const selectedCustomer = ref(null);
const showCustomerDetailModal = ref(false);
const showCreateCustomerModal = ref(false);

const columns = ref([
  { key: "loai_khach", label: "Loại khách", visible: false },
  { key: "chi_nhanh_tao", label: "Chi nhánh tạo", visible: false },
  { key: "ma_khach_hang", label: "Mã KH", visible: true },
  { key: "ten_khach_hang", label: "Tên khách hàng", visible: true },
  { key: "dien_thoai", label: "Điện thoại", visible: true },
  { key: "dia_chi", label: "Địa chỉ", visible: false },
  { key: "khu_vuc_giao_hang", label: "Khu vực", visible: false },
  { key: "phuong_xa", label: "Phường/Xã", visible: false },
  { key: "cong_ty", label: "Công ty", visible: false },
  { key: "ma_so_thu", label: "MST", visible: false },
  { key: "so_cmnn_cccd", label: "CMND/CCCD", visible: false },
  { key: "ngay_sinh", label: "Ngày sinh", visible: false },
  { key: "gioi_tinh", label: "Giới tính", visible: false },
  { key: "email", label: "Email", visible: true },
  { key: "facebook", label: "Facebook", visible: false },
  { key: "nhom_khach_hang", label: "Nhóm KH", visible: false },
  { key: "ghi_chu", label: "Ghi chú", visible: false },
  { key: "nguoi_phu_trach", label: "Người phụ trách", visible: false },
  { key: "nguoi_tao", label: "Người tạo", visible: false },
  { key: "ngay_tao", label: "Ngày tạo", visible: false },
  { key: "ngay_giao_dich_cuoi", label: "GD cuối", visible: false },
  { key: "so_ngay_no", label: "Số ngày nợ", visible: false },
  { key: "no_can_thu_hien_tai", label: "Nợ", visible: false, type: "currency" },
  { key: "tong_ban", label: "Tổng bán", visible: false, type: "currency" },
  {
    key: "tong_ban_tru_tra_hang",
    label: "Doanh thu thuần",
    visible: false,
    type: "currency",
  },
  { key: "trang_thai", label: "Trạng thái", visible: true, type: "status" },
]);
const renderValue = (item, col) => {
  const value = item[col.key];
  if (col.type === "currency") {
    return formatCurrency(value);
  }
  if (col.type === "status") {
    const s = formatStatus(value);
    return `<span class="status-badge ${s.class}">${s.text}</span>`;
  }

  return value || "-";
};
const formatStatus = (status) => {
  const statusMap = {
    1: { text: "Hoạt động", class: "status-active" },
    0: { text: "Tạm ngưng", class: "status-inactive" },
  };

  return statusMap[status] || { text: "-", class: "status-unknown" };
};

const visibleColumns = computed(() =>
  columns.value.filter((col) => col.visible),
);

const triggerFileInput = () => {
  fileInput.value.click();
};
const handleFileImport = async (event) => {
  const file = event.target.files[0];
  if (!file) return;
  const fileName = file.name.toLowerCase();
  if (
    !fileName.endsWith(".xlsx") &&
    !fileName.endsWith(".xls") &&
    !fileName.endsWith(".csv")
  ) {
    toast.error("Vui lòng chọn file Excel (.xlsx, .xls) hoặc .csv");
    return;
  }
  const formData = new FormData();
  formData.append("file", file);
  loading.value = true;
  try {
    const response = await importApi.importCustomers(formData);
    toast.success(
      `Đã nhập thành công ${response.data.imported_count} khách hàng`,
    );
    fetchCustomers();
    event.target.value = "";
  } catch (error) {
    event.target.value = "";
    const errorMessage =
      error.response?.data?.message || "Có lỗi xảy ra khi nhập dữ liệu";
    toast.error(errorMessage);
    console.error(error);
  } finally {
    loading.value = false;
  }
};
const fetchCustomers = async () => {
  loading.value = true;
  try {
    const response = await adminApi.getCustomers({
      page: pagination.current_page,
      search: search.value,
      status: status.value,
      per_page: pagination.per_page || 15,
    });
    customers.value = response.data.data.data || response.data;
    const { ...paginationMeta } = response.data.data;
    updatePagination(paginationMeta);
  } catch (error) {
    const errorMessage =
      error.response?.data?.error || "Lỗi tải dữ liệu khách hàng";
    toast.error(errorMessage);
  } finally {
    loading.value = false;
  }
};
const viewCreateCustomerModal = () => {
  showCreateCustomerModal.value = true;
};
const viewDetailCustomer = (customer) => {
  showCustomerDetailModal.value = true;
  selectedCustomer.value = customer;
};

const { pagination, changePage, visiblePages, updatePagination } =
  usePagination(fetchCustomers);

onMounted(fetchCustomers);
</script>

<style scoped>
/* ===== PAGE ===== */
.customer-page {
  background: #f5f6fa;
  padding: 20px;
  border-radius: 12px;
}

/* ===== HEADER ===== */
.customer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.customer-header h1 {
  font-size: 22px;
  font-weight: 700;
}

.btn-add {
  background: linear-gradient(135deg, #f36c21, #ffd54f);
  color: white;
  border: none;
  padding: 10px 18px;
  border-radius: 20px;
  cursor: pointer;
  transition: 0.3s;
}

.btn-add:hover {
  transform: scale(1.05);
}

/* ===== FILTER ===== */
.customer-filter {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}

.customer-filter input,
.customer-filter select,
.column-toggle {
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #ddd;
  outline: none;
}

.btn-search {
  background: #333;
  color: white;
  border: none;
  padding: 8px 14px;
  border-radius: 8px;
}

.column-toggle {
  position: relative;
}
.column-panel {
  position: absolute;
  top: 35px;
  right: 0;
  width: 250px;
  max-height: 300px;
  overflow-y: auto;
  background-color: white;
  border: 1px solid #ddd;
  padding: 10px;
}
.column-panel label {
  display: block;
  margin-bottom: 5px;
}

/* ===== TABLE ===== */
.customer-table {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

table {
  width: 100%;
  border-collapse: collapse;
}

thead {
  background: #111;
  color: white;
}

th,
td {
  padding: 12px 14px;
  text-align: left;
  font-size: 14px;
}

tbody tr {
  border-bottom: 1px solid #eee;
}

tbody tr:hover {
  background: #f9f9f9;
}

/* ===== IMAGE ===== */
.customer-img {
  width: 50px;
  height: 50px;
  border-radius: 8px;
  object-fit: cover;
}

/* ===== STATUS ===== */
.status-badge {
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
}

.status-active {
  background-color: #dcfce7;
  color: #166534;
}

.status-inactive {
  background-color: #fee2e2;
  color: #991b1b;
}

.status-unknown {
  background-color: #f3f4f6;
  color: #4b5563;
}

/* ===== ACTION BUTTON ===== */
.btn-edit {
  background: #2196f3;
  color: white;
  border: none;
  padding: 5px 10px;
  border-radius: 6px;
  margin-right: 5px;
}

.btn-delete {
  background: #f44336;
  color: white;
  border: none;
  padding: 5px 10px;
  border-radius: 6px;
}
</style>
